import React, { useMemo, useState } from "react";
import { Grid, IconButton } from "@mui/material";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxButton from "components/UI/AcxButton";
import theme from "Theme/AppTheme";
import { AcxViewSqlDialog } from "components/UI/AcxViewSqlDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AcxMenu, { StyledMenuLabel } from "components/UI/Menu/AcxMenu";
import MessageIcon from "@mui/icons-material/Message";

import { SignalDataEndpoint } from "components/Signals/SignalsModels";
import DrilldownStore from "./DrilldownStore";
import EvaluateConversationsDialog from "./EvaluateConversationsDialog";
import BaseObjectStore from "../BaseObjectStore";
import { DrilldownSource } from "./DrilldownTypes";

type Props = {
    drilldownStore: DrilldownStore | null;
    baseObjectStore: BaseObjectStore<any, any> & DrilldownSource;
};

const DrilldownDialog = observer(
    ({ drilldownStore, baseObjectStore }: Props) => {
        const navigate = useNavigate();
        const [moreMenuAnchorEl, setMoreMenuAnchorEl] =
            useState<null | HTMLElement>(null);
        const [showSqlDialog, setShowSqlDialog] = useState(false);
        const normalizedDataEndpoint = useMemo(() => {
            const endpoint = baseObjectStore.dataEndpoint;
            if (!endpoint) return undefined;
            if (endpoint.includes("?")) {
                return endpoint.substring(0, endpoint.indexOf("?")) as
                    | SignalDataEndpoint
                    | undefined;
            }
            return endpoint as SignalDataEndpoint;
        }, [baseObjectStore.dataEndpoint]);

        if (!drilldownStore) return null;

        const evaluateButtonDisabled =
            drilldownStore.selectedDataDgStore.selectedRows.length === 0;

        const showSQL =
            !!drilldownStore.drilldownSql &&
            drilldownStore.authStore.isUserUltra();

        const showViewInConvosButton =
            normalizedDataEndpoint &&
            baseObjectStore.config.pointSelectActions.viewInConvos;

        function handleMoreMenuClick(
            event: React.MouseEvent<HTMLButtonElement>,
        ) {
            setMoreMenuAnchorEl(event.currentTarget);
        }

        function handleMoreMenuClose() {
            setMoreMenuAnchorEl(null);
        }

        return (
            <AcxDialog
                title={drilldownStore.drilldownDialogTitle || "Untitled"}
                subTitle={
                    drilldownStore.drilldownSubtitle
                        ? drilldownStore.drilldownSubtitle
                        : ""
                }
                dialogContentChildren={
                    <Grid
                        container
                        item
                        style={{
                            height: "606px",
                            width: "80vw",
                            maxWidth: "calc(1536px - 48px)",
                        }}
                    >
                        <Grid
                            item
                            container
                            alignItems={"center"}
                            justifyContent={"flex-end"}
                            xs={12}
                            gap={1}
                        >
                            {showViewInConvosButton && (
                                <AcxButton
                                    onClick={async () => {
                                        if (
                                            drilldownStore.selectedDataPoint ||
                                            drilldownStore.selectedTopic ||
                                            drilldownStore.selectedDriver
                                        ) {
                                            drilldownStore.viewInConvos();

                                            navigate(
                                                "/app/conversations?fromDomain=signals",
                                            );

                                            drilldownStore.closeDrilldownDialog();
                                        }
                                    }}
                                    startIcon={<MessageIcon />}
                                    id="view-in-conversations"
                                >
                                    View in Conversations
                                </AcxButton>
                            )}
                            {showSQL && (
                                <>
                                    <IconButton
                                        id={`signals-drilldown-Menu`}
                                        style={{
                                            color: theme.palette.black.main,
                                            height: "40px",
                                        }}
                                        onClick={handleMoreMenuClick}
                                        size="small"
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <AcxViewSqlDialog
                                        sqlStatements={drilldownStore.drilldownSql!.split(
                                            "==========",
                                        )}
                                        isModalOpen={showSqlDialog}
                                        onClose={() => setShowSqlDialog(false)}
                                    />
                                    <AcxMenu
                                        anchorElement={moreMenuAnchorEl}
                                        menuItems={[
                                            {
                                                id: "signals-show-sql-menu-item",
                                                props: {
                                                    onClick: () => {
                                                        setShowSqlDialog(true);
                                                        handleMoreMenuClose();
                                                    },
                                                    disabled:
                                                        drilldownStore.anyTaskLoading,
                                                },
                                                label: (
                                                    <StyledMenuLabel>
                                                        View SQL
                                                    </StyledMenuLabel>
                                                ),
                                            },
                                        ]}
                                        onMenuClose={handleMoreMenuClose}
                                    />
                                </>
                            )}
                        </Grid>

                        <AcxDataGrid
                            dataGridStore={drilldownStore.selectedDataDgStore}
                        />

                        <EvaluateConversationsDialog
                            drilldownStore={drilldownStore}
                        />
                    </Grid>
                }
                isOpen={drilldownStore.showSelectedDataDialog}
                onClose={() => drilldownStore.closeDrilldownDialog()}
                maxWidth={false}
                disableEnforceFocus
                hideCancelButton={
                    drilldownStore.selectedTopic || drilldownStore.selectedDriver
                        ? true
                        : false
                }
            >
                {/* Every grid listing conversations can evaluate them, including the ones
                    reached through a Topic or a Driver. */}
                {drilldownStore.isShowingConversations ? (
                    <AcxButton
                        onClick={() =>
                            drilldownStore.setShowEvaluateDialog(true)
                        }
                        disabled={evaluateButtonDisabled}
                        id="signals-drilldown-evaluate"
                        containerStyle={{
                            marginRight: "16px",
                        }}
                        style={{
                            ...(evaluateButtonDisabled && {
                                backgroundColor: theme.palette.gray[100],
                                color: theme.palette.gray[400],
                            }),
                        }}
                    >
                        Evaluate
                    </AcxButton>
                ) : undefined}
            </AcxDialog>
        );
    },
);

export default DrilldownDialog;
